import {useQuery} from '@tanstack/react-query'
import {useServices} from '@/services/context'
import {AutoAssignRuleType} from '@/types/auto-assign-rule'
import type {AutoAssignRuleRequest} from '@/types/auto-assign-rule'
import type {AccessProfile} from '@/types/access-profile'
import type {FunctionGroup} from '@/types/function-group'

export interface UseAutoAssignRuleRequestResult {
    request: AutoAssignRuleRequest | undefined
    linkedProfiles: AccessProfile[]
    linkedGroups: FunctionGroup[]
    isLoading: boolean
    isProfilesLoading: boolean
    isGroupsLoading: boolean
    error: Error | null
}

/**
 * Composite read hook for the AutoAssignRuleRequestDetail page.
 *
 * Fetches the rule request, then resolves linked access profiles or function
 * groups based on {@link AutoAssignRuleType}. Returns a flat shape that
 * 603.05 (page) and 603.08 (wizard) consume directly.
 */
export function useAutoAssignRuleRequest(id: string | undefined): UseAutoAssignRuleRequestResult {
    const services = useServices()

    const {data: request, isLoading, error} = useQuery({
        queryKey: ['rule-request', id],
        queryFn: () => services.autoAssignRules.detailRequest(id!),
        enabled: !!id,
    })

    const isFunctionGroupRule = request?.ruleType === AutoAssignRuleType.FUNCTION_GROUP

    const {data: linkedProfiles = [], isLoading: isProfilesLoading} = useQuery({
        queryKey: ['access-profiles', 'by-ids', request?.profileIds],
        queryFn: async () => {
            if (!request?.profileIds || request.profileIds.length === 0) return [] as AccessProfile[]
            const profilePromises = request.profileIds.map(profileId =>
                services.accessProfiles.detail(profileId)
                    .catch(() => null),
            )
            const results = await Promise.all(profilePromises)
            return results.filter((p): p is AccessProfile => p !== null)
        },
        enabled: !!request?.profileIds && request.profileIds.length > 0 && !isFunctionGroupRule,
    })

    const {data: linkedGroups = [], isLoading: isGroupsLoading} = useQuery({
        queryKey: ['function-groups', 'by-ids', request?.functionGroupIds],
        queryFn: async () => {
            if (!request?.functionGroupIds || request.functionGroupIds.length === 0) return [] as FunctionGroup[]
            const promises = request.functionGroupIds.map(functionGroupId =>
                services.functionGroups.detail(functionGroupId)
                    .catch(() => null),
            )
            const results = await Promise.all(promises)
            return results.filter((fg): fg is FunctionGroup => fg !== null)
        },
        enabled: !!request?.functionGroupIds && request.functionGroupIds.length > 0 && isFunctionGroupRule,
    })

    return {
        request,
        linkedProfiles,
        linkedGroups,
        isLoading,
        isProfilesLoading,
        isGroupsLoading,
        error: error as Error | null,
    }
}
